import React, { useState } from "react";
import { MessageSquareText, Send, Sparkles, Zap } from "lucide-react";
import { askAi } from "../../api";
import OneTapTrade from "./OneTapTrade";
import { toast } from "sonner";

const QUICK = [
  "Conviene comprare o vendere XAUUSD adesso?",
  "Il trend attuale dell'oro è rialzista o ribassista?",
  "Dove metto stop loss e take profit per un'operazione ora?",
  "Devo chiudere le posizioni aperte?",
];

const COLORS = { BUY: "#10B981", SELL: "#EF4444", WAIT: "#F59E0B" };

export default function AiOpinion({ onCreateSignal, canTrade = true, onSent }) {
  const [question, setQuestion] = useState(QUICK[0]);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const ask = async (q) => {
    const text = (q ?? question).trim();
    if (!text) return;
    setQuestion(text);
    setBusy(true);
    try {
      const r = await askAi(text);
      setRes(r);
      toast.success(`AI: ${r.decision} XAUUSD`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI non disponibile");
    } finally {
      setBusy(false);
    }
  };

  const lead = res?.lead;
  const color = COLORS[res?.decision] || COLORS.WAIT;
  const okAnswers = (res?.answers || []).filter((a) => !a.error);
  const tradable = res && res.decision !== "WAIT" && lead?.sl > 0 && (lead?.tps || []).length > 0;

  const createSignal = () => {
    const lo = lead.entryLow > 0 ? lead.entryLow : Number(res.market?.bid) - 1;
    const hi = lead.entryHigh > 0 ? lead.entryHigh : Number(res.market?.bid) + 1;
    onCreateSignal?.({
      side: res.decision,
      volume: 0.1,
      openLow: Math.min(lo, hi),
      openHigh: Math.max(lo, hi),
      sl: lead.sl,
      tps: lead.tps,
      reason: `AI ${lead.provider}: ${lead.reason}`.slice(0, 240),
    });
  };

  return (
    <div id="ai-opinion" data-testid="ai-opinion-panel" className="panel p-5 space-y-4 float-in">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        <span className="w-6 h-6 rounded-md grid place-items-center bg-amber-500/15 text-amber-400">
          <MessageSquareText size={13} />
        </span>
        Chiedi all'AI · parere in tempo reale
      </div>

      <OneTapTrade canTrade={canTrade} onSent={onSent} onOpenSetup={(s) => onCreateSignal?.(s)} />

      <div className="flex flex-wrap gap-1.5">
        {QUICK.map((q) => (
          <button
            key={q}
            data-testid="ai-quick-question"
            onClick={() => ask(q)}
            disabled={busy}
            className="text-[10px] px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/10 text-slate-300 hover:border-amber-400/50 hover:text-amber-200 transition-colors disabled:opacity-50"
          >
            {q}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          data-testid="ai-question-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="Fai una domanda sull'oro…"
          className="flex-1 min-w-0 bg-[#0F1728] border border-white/10 rounded-lg px-3 py-2 text-xs text-slate-100 outline-none focus:border-amber-400/60"
        />
        <button
          data-testid="ai-ask-button"
          onClick={() => ask()}
          disabled={busy || !question.trim()}
          className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-2 rounded-lg bg-amber-400 text-[#0B1220] hover:bg-amber-300 disabled:opacity-60 transition-colors"
        >
          {busy ? <Sparkles size={13} className="animate-pulse" /> : <Send size={13} />}
          {busy ? "Analizzo…" : "Chiedi"}
        </button>
      </div>

      {res && (
        <div data-testid="ai-opinion-result" className="rounded-xl border border-white/10 bg-white/[0.025] p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <span data-testid="ai-opinion-decision" className="font-mono text-2xl font-extrabold" style={{ color }}>
              {res.decision} XAUUSD
            </span>
            <span className="text-[10px] font-mono text-slate-400 text-right">
              Confidenza {Math.round((res.confidence || 0) * 100)}% · {lead?.horizon || "intraday"} · rischio {lead?.risk || "—"}
              <br />
              {lead?.provider} · {lead?.model}
            </span>
          </div>
          <p data-testid="ai-opinion-reason" className="text-xs text-slate-200 leading-relaxed">{lead?.reason}</p>
          {res.decision !== "WAIT" && lead?.sl > 0 && (
            <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
              <Stat label="Ingresso" value={`${fmt(lead.entryLow)} – ${fmt(lead.entryHigh)}`} />
              <Stat label="Stop Loss" value={fmt(lead.sl)} tone="text-rose-300" />
              <Stat label="Take Profit" value={(lead.tps || []).map(fmt).join(" · ") || "—"} tone="text-emerald-300" />
            </div>
          )}
          {lead?.invalidation && (
            <p className="text-[10px] text-slate-400"><span className="text-slate-500">Invalidazione:</span> {lead.invalidation}</p>
          )}
          {okAnswers.length > 1 && (
            <div data-testid="ai-opinion-votes" className="grid gap-1.5">
              {res.answers.map((a, i) => {
                const dec = a.error ? "ERROR" : a.decision;
                const c = dec === "BUY" ? "text-emerald-400" : dec === "SELL" ? "text-rose-400" : dec === "WAIT" ? "text-amber-400" : "text-slate-500";
                return (
                  <div key={i} data-testid="ai-opinion-vote" className="flex items-center justify-between text-[10px] bg-[#0F1728] border border-white/8 rounded-md px-2.5 py-1.5">
                    <span className="text-slate-300 truncate">{a.provider} <span className="text-slate-500">· {a.model}</span></span>
                    <span className={`font-mono font-bold ${c}`}>{dec}{!a.error ? ` · ${Math.round(a.confidence * 100)}%` : ""}</span>
                  </div>
                );
              })}
              <p className="text-[9px] text-slate-500">Accordo {Math.round((res.agreement || 0) * 100)}% su {okAnswers.length} modelli</p>
            </div>
          )}
          {tradable && (
            <button
              data-testid="ai-create-signal-button"
              onClick={createSignal}
              disabled={!canTrade}
              className="w-full flex items-center justify-center gap-2 text-[11px] font-bold px-3 py-2.5 rounded-lg text-[#0B1220] disabled:opacity-50 transition-colors"
              style={{ background: color }}
            >
              <Zap size={13} /> Crea segnale {res.decision} da questo parere
            </button>
          )}
          <p className="text-[9px] text-slate-500">
            Prezzo analizzato {fmt(res.market?.bid)} · {res.answers?.filter((a) => !a.error).length}/{res.answers?.length} modelli · parere di supporto, non consiglio finanziario.
          </p>
        </div>
      )}
    </div>
  );
}

const fmt = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v).toFixed(2) : "—");

function Stat({ label, value, tone = "text-slate-100" }) {
  return (
    <div className="bg-[#0F1728] rounded-lg px-2.5 py-2 border border-white/8">
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-bold ${tone} break-words`}>{value}</div>
    </div>
  );
}
